<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              參數設定 
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              CYBERBIZ 會員查詢 
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>

    <b-card>
      <div class="row mb-2 align-items-end">
        <div class="col-8 d-flex items-center">
          <h4 class="font-weight-bold">CYBERBIZ 會員查詢</h4>
        </div>
      </div>

      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
      >
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
          label="品牌"
        >
          <b-form-select
            v-model="selectedMerchantId"
            :options="merchantOptions"
            :disabled="isFetchingMerchants"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                {{ isFetchingMerchants ? '載入中...' : '請選擇品牌' }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- 手機-->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
        >
          <template #label>
            <b-form-radio
              v-model="searchType"
              value="phone"
              @change="handleSearchTypeChange"
            >
              手機
            </b-form-radio>
          </template>
          <b-form-input
            type="text"
            v-model="phoneQuery"
            placeholder="請輸入手機號碼"
            :disabled="searchType !== 'phone'"
            @keyup.enter="handleSearch"
          ></b-form-input>
        </b-form-group>

        <!-- 會員ID-->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
        >
          <template #label>
            <b-form-radio
              v-model="searchType"
              value="customer_id"
              @change="handleSearchTypeChange"
            >
              會員ID
            </b-form-radio>
          </template>
          <b-form-input
            type="text"
            v-model="customerIdQuery"
            placeholder="請輸入會員ID Ex. 12345678"
            :disabled="searchType !== 'customer_id' || isCustomerIdFromApi"
            @keyup.enter="handleSearch"
          ></b-form-input>
        </b-form-group>

        <!-- 查詢按鈕 -->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
          label=""
        >
          <b-button
            variant="info"
            @click="handleSearch"
            :disabled="isLoading || !canSearch"
          >
            查詢
          </b-button>
        </b-form-group>
        <editor
          class="mb-2 mr-sm-2 mb-sm-0"
          v-model="result"
          width="100%"
          height="400px"
          @init="editorInit"
          lang="json"
          theme="chrome"
          :disabled="!editable"
        >
        </editor>
      </b-overlay>
    </b-card>
  </div>
</template>

<style scoped lang="scss">
.group + .group {
  border-top: 1px solid #cdd6dc !important;
}
</style>
<script>
import { mapState } from "vuex";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import editor from "vue2-ace-editor";
import cyberbizApi from "@/apis/cyberbiz";

export default {
  components: { editor },
  data() {
    return {
      // 搜尋類型：'phone' 或 'customer_id'
      searchType: 'phone',
      searchTypeOptions: [
        { text: '手機', value: 'phone' },
        { text: '會員ID', value: 'customer_id' },
      ],
      phoneQuery: '',
      customerIdQuery: '',
      // 標記 customerIdQuery 是否來自 API 回傳
      isCustomerIdFromApi: false,
      result: '',
      isLoading: false,
      isSubmmiting: false,
      canEdit: false,
      // Merchants dropdown
      merchants: [],
      selectedMerchantId: null,
      isFetchingMerchants: false,
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    orgId() {
      return this.organization.id;
    },
    editable() {
      return this.canEdit;
    },
    merchantOptions() {
      return this.merchants.map((m) => ({
        value: m.merchant_id,
        text: m.merchant_name,
      }));
    },
    canSearch() {
      // 1. 沒選品牌 -> false
      if (!this.selectedMerchantId) {
        return false;
      }

      // 2. 手機模式 -> 檢查手機欄位是否有內容
      if (this.searchType === 'phone') {
        return this.phoneQuery && this.phoneQuery.trim().length > 0;
      }

      // 3. 會員ID模式 -> 檢查ID欄位是否有內容
      return this.customerIdQuery && this.customerIdQuery.trim().length > 0;
    },
  },
  watch: {
    phoneQuery() {
      if (this.searchType === 'phone' && this.isCustomerIdFromApi) {
        this.customerIdQuery = '';
        this.isCustomerIdFromApi = false;
      }
    },
  },
  mounted() {
    if (!this.checkPermission([consts.CYBERBIZ_SETTING_QUERY])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33",
        })
        .then(() => {
          this.$router.push({
            name: "DashboardHome",
          });
        });
      return;
    }
    this.fetchMerchants();
  },
  methods: {
    editorInit() {
      require("brace/ext/language_tools"); //language extension prerequsite...
      require("brace/mode/json");
      require("brace/mode/html");
      require("brace/theme/chrome");
    },
    async fetchMerchants() {
      try {
        this.isFetchingMerchants = true;
        const { data } = await cyberbizApi.getMerchants(this.orgId);
        this.merchants = data.data || [];
        // 如果只有一個品牌，自動選取
        if (this.merchants.length === 1) {
          this.selectedMerchantId = this.merchants[0].merchant_id;
        }
      } catch (error) {
        console.error("Failed to fetch merchants:", error);
        this.$swal.fire({
          type: "error",
          text: "載入品牌列表失敗",
        });
      } finally {
        this.isFetchingMerchants = false;
      }
    },
    handleSearchTypeChange() {
      this.phoneQuery = '';
      this.customerIdQuery = '';
      this.isCustomerIdFromApi = false;
      this.result = '';
    },
    async handleSearch() {
      // 驗證：必須選擇品牌
      if (!this.selectedMerchantId) {
        this.$swal.fire({
          type: 'warning',
          text: '請先選擇品牌',
        });
        return;
      }

      // 根據搜尋類型取得查詢值
      const isPhoneSearch = this.searchType === 'phone';
      const query = isPhoneSearch
        ? this.phoneQuery?.trim()
        : this.customerIdQuery?.trim();

      if (!query) {
        this.$swal.fire({
          type: 'warning',
          text: isPhoneSearch ? '請輸入手機號碼' : '請輸入會員ID',
        });
        return;
      }

      // 手機格式驗證：09 開頭的 10 位數字
      if (isPhoneSearch && !/^09\d{8}$/.test(query)) {
        this.$swal.fire({
          type: 'warning',
          text: '請輸入有效的手機號碼（09 開頭，共 10 位數字）',
        });
        return;
      }

      try {
        this.isLoading = true;
        this.isSubmmiting = true;
        this.result = '查詢中...';

        const params = {
          merchant_id: this.selectedMerchantId,
        };

        if (isPhoneSearch) {
          params.phone = query;
        } else {
          params.customer_id = query;
        }

        const result = await cyberbizApi.searchCustomer(this.orgId, params);

        if (isPhoneSearch && result.data?.customer_id) {
          this.customerIdQuery = result.data.customer_id;
          this.isCustomerIdFromApi = true;
        }

        // 顯示回傳的 data
        if (result.data?.data) {
          this.result = JSON.stringify(result.data.data, null, 2);
        } else {
          this.result = JSON.stringify(result.data, null, 2);
        }
      } catch (error) {
        console.error('Customer search failed:', error);
        if (error.response && error.response.data) {
          this.result = JSON.stringify(error.response.data, null, 2);
        } else {
          this.result = JSON.stringify(
            {
              error: '查詢失敗',
              message: error.message || '未知錯誤',
            },
            null,
            2
          );
        }
      } finally {
        this.isLoading = false;
        this.isSubmmiting = false;
      }
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>
